import {ThemeProvider} from '@primer/react-brand'
import type React from 'react'
import {useMemo} from 'react'

import {useAppPayload} from '@github-ui/react-core/use-app-payload'
import {useFeatureFlag} from '@github-ui/react-core/use-feature-flag'
import {ContentfulFormContext} from '@github-ui/swp-core/components/contentful/ContentfulForm'
import {OctocaptchaContext} from '@github-ui/swp-core/components/forms/OctocaptchaContext'
import {ConsentExperienceContext} from '@github-ui/swp-core/components/forms/ConsentExperienceContext'
import {useConsentExperience} from '@github-ui/swp-core/components/forms/useConsentExperience'

import type {Country} from '@github-ui/swp-core/components/forms/ConsentExperience/types'

import {ErrorBoundary} from './components/ErrorBoundary/ErrorBoundary'
import {NinetailedConfigContext} from '@github-ui/swp-core/contexts/NinetailedConfigContext'

/**
 * The App component is used to render content which should be present on _all_ routes within this app
 */
export function App({children}: {children?: React.ReactNode}) {
  const payload = useAppPayload<{
    marketingFormsApiHost?: string
    octocaptchaHost?: string
    marketingTargetedCountries: Country[]
    locale?: string
    ninetailedClientId?: string
    ninetailedEnvironment?: string
  } | null>()

  const locale = payload?.locale
  const octocaptchaCtx = useMemo(
    () => ({hostName: payload?.octocaptchaHost ?? '', originPage: 'marketing_forms', locale}),
    [payload?.octocaptchaHost, locale],
  )

  const contentfulFormCtx = useMemo(
    () => ({
      marketingFormsApiHost: payload?.marketingFormsApiHost,
      marketingTargetedCountries: payload?.marketingTargetedCountries,
    }),
    [payload?.marketingFormsApiHost, payload?.marketingTargetedCountries],
  )

  const consent = useConsentExperience(null)
  const consentExperienceCtx = useMemo(
    () => ({
      marketingTargetedCountries: payload?.marketingTargetedCountries ?? [],
      selectedCountry: consent.selectedCountry,
      setSelectedCountry: consent.setSelectedCountry,
    }),
    [payload?.marketingTargetedCountries, consent.selectedCountry, consent.setSelectedCountry],
  )

  // Feature flag for Ninetailed integration
  const isNinetailedEnabled = useFeatureFlag('landing_pages_ninetailed')

  // Extract Ninetailed config values for stable dependency tracking
  const ninetailedClientId = payload?.ninetailedClientId
  const ninetailedEnvironment = payload?.ninetailedEnvironment

  // Ninetailed configuration - only enable if feature flag and clientId are provided
  const ninetailedConfig = useMemo(() => {
    if (!isNinetailedEnabled || !ninetailedClientId) {
      return null
    }

    // Ninetailed expects ISO 639-1 language codes (e.g., 'en', 'es', 'fr')
    // Extract language code from locale like 'en-US' -> 'en'
    const ninetailedLocale = locale ? new Intl.Locale(locale).language : 'en'

    return {
      clientId: ninetailedClientId,
      environment: ninetailedEnvironment || 'main',
      locale: ninetailedLocale,
    }
  }, [isNinetailedEnabled, ninetailedClientId, ninetailedEnvironment, locale])

  // Content to be rendered within the context providers
  const content = (
    <NinetailedConfigContext value={ninetailedConfig}>
      <ContentfulFormContext value={contentfulFormCtx}>
        <OctocaptchaContext value={octocaptchaCtx}>
          <ConsentExperienceContext value={consentExperienceCtx}>
            <ThemeProvider dir="ltr">{children}</ThemeProvider>
          </ConsentExperienceContext>
        </OctocaptchaContext>
      </ContentfulFormContext>
    </NinetailedConfigContext>
  )

  return <ErrorBoundary>{content}</ErrorBoundary>
}
